import {
  Badge,
  Button,
  CopyButton,
  Flex,
  Grid,
  Group,
  Image,
  Kbd,
  Modal,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import axios from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import useAuthStore from "@/store/useAuthStore";
import { LOAD_TYPE, UNLOAD_TYPE } from "@/utils/constants";
import { getDistanceFromTmap } from "@/utils/functions";
import { parseMoney, parsePhone } from "@/utils/helpers";
import { deleteModal } from "./deleteModal";
import ListHeader from "./ListHeader";
import ListRow from "./ListRow";
import OrderDetailBoseModal from "./OrderDetailBoseModal";
import OrderDetailCompanyInfoModal from "./OrderDetailCompanyInfoModal";
import OrderDetailStreetViewModal from "./OrderDetailStreetViewModal";
import OrderImagesViewer from "./OrderImagesViewer";

export default function OrderDetail({ id, mutate, buttonRef, companyId }) {
  const [opened, { open, close }] = useDisclosure();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const { data, mutate: refreshOrder } = useSWR(opened ? [`/orders/${id}?populate=*`] : null);

  const order = data?.data?.attributes || {};

  async function handleCancel() {
    deleteModal({
      title: "화물 취소",
      description: "화물을 취소하시겠습니까?",
      labels: { confirm: "화물 취소하기", cancel: "닫기" },
      onConfirm: async () => {
        await axios({
          url: "/orders/" + id,
          method: "PUT",
          data: {
            data: {
              status: "canceled",
            },
          },
        });

        await axios({
          url: "/notifications",
          method: "post",
          data: {
            data: {
              name: "오더 취소",
              type: "order-cancel",
              content: `코드: ${id}`,
              requestUser: user.name + " (" + parsePhone(user.phoneNumber) + ")",
              requestCompany: order?.company?.data?.attributes?.name || "미소속",
              relationType: "order",
              relationId: id,
              isDone: false,
            },
          },
        });

        mutate();
        toast.success("화물이 취소되었습니다");
        close();
      },
    });
  }

  async function getDistance() {
    if (!order.startLat || !order.startLng || !order.endLat || !order.endLng)
      return toast.error("좌표가 없습니다");

    const loading = toast.loading("거리 계산 중...");
    const { totalDistance, totalTime, totalFare } = await getDistanceFromTmap({
      startLat: order.startLat,
      startLng: order.startLng,
      endLat: order.endLat,
      endLng: order.endLng,
    });

    await axios({
      url: "/orders/" + id,
      method: "PUT",
      data: {
        data: {
          distance: Number(totalDistance),
          time: Number(totalTime),
          fare: Number(totalFare),
        },
      },
    });

    mutate();
    refreshOrder();
    toast.success("거리 계산 완료", { id: loading });
  }

  return (
    <>
      <Button compact onClick={open} ref={buttonRef}>
        상세
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group spacing="xs">
            <CopyButton value={String(id)}>
              {({ copy }) => (
                <Kbd
                  onClick={() => {
                    copy();
                    toast.success("배차코드 복사 완료");
                  }}
                  style={{ cursor: "pointer" }}
                >
                  #{id}
                </Kbd>
              )}
            </CopyButton>
            <Text fw={500}>오더 상세</Text>
          </Group>
        }
        size="xl"
      >
        <Grid gutter="xl">
          <Grid.Col xs={12} md={6}>
            <ListHeader title="화물(상차품) 사진" />
            <Image
              src={
                order.productImages?.data?.[order.productImages?.data?.length - 1]?.attributes?.url
              }
              width="100%"
              height={200}
              withPlaceholder
              radius="sm"
              mb="md"
              sx={{ border: "1px solid gray", borderRadius: "8px" }}
            />

            <ListHeader title="상하차지 정보" />
            <ListRow label="상차지명" val={order.startName} />
            <ListRow label="상차지주소">
              <Flex gap={4} align="center">
                <Text size="sm" fw={700}>
                  {order.startAddress}
                </Text>
                <OrderDetailStreetViewModal lat={order.startLat} lng={order.startLng} id={id} />
              </Flex>
            </ListRow>
            <ListRow label="상차지 담당자" val={parsePhone(order.startPhoneNumber)} />
            <ListRow label="하차지명" val={order.endName} />
            <ListRow label="하차지주소">
              <Flex gap={4} align="center">
                <Text size="sm" fw={700}>
                  {order.endAddress}
                </Text>
                <OrderDetailStreetViewModal lat={order.endLat} lng={order.endLng} id={id} />
              </Flex>
            </ListRow>
            <ListRow label="하차지 담당자" val={parsePhone(order.endPhoneNumber)} isLast />

            <ListHeader title="상하차 요청 일시" mt="xl" />
            <ListRow label="상차 요청">
              <Flex gap="xs" align="center">
                <Badge color={LOAD_TYPE.find((x) => x.value === order.startType)?.color}>
                  {LOAD_TYPE.find((x) => x.value === order.startType)?.label}
                </Badge>
                <Text size="sm" fw={700}>
                  {dayjs(order.startDate).format("YYYY-MM-DD")}
                </Text>
                <Text size="sm" color="dimmed">
                  {order.loadTime || "-"}
                </Text>
              </Flex>
            </ListRow>
            {/* <ListRow label="상차시간" val={order.loadTime || "-"} /> */}
            <ListRow label="하차 요청" isLast>
              <Flex gap="xs" align="center">
                <Badge color={UNLOAD_TYPE.find((x) => x.value === order.endType)?.color}>
                  {UNLOAD_TYPE.find((x) => x.value === order.endType)?.label}
                </Badge>
                {order.endDate && (
                  <Text size="sm" fw={700}>
                    {dayjs(order.endDate).format("YYYY-MM-DD")}
                  </Text>
                )}
                <Text size="sm" color="dimmed">
                  {order.unloadTime || "-"}
                </Text>
              </Flex>
            </ListRow>
            {/* <ListRow label="하차시간" val={order.unloadTime || "-"} isLast /> */}
          </Grid.Col>

          <Grid.Col xs={12} md={6}>
            <ListHeader title="화물 정보" />
            <ListRow label="화물 상차 중량" val={order.productWeight + "톤"} />
            <ListRow label="화물 내용" val={order.productContent} />
            <ListRow label="기타 요청사항" val={order.memo || "-"} />
            <ListRow label="요청 차량" val={`${order.carWeight} / ${order.carType}`} />
            {/* <ListRow label="차종" val={order.carType} /> */}
            <ListRow label="상/하차 방법" val={`${order.loadType} / ${order.unloadType}`} />
            {/* <ListRow label="하차 방법" val={order.unloadType} /> */}
            <ListRow label="옵션" isLast>
              <Flex gap="xs">
                {order.isReceiptRequired && <Badge color="indigo">인수증</Badge>}
                {order.isRoundTrip && <Badge color="grape">왕복</Badge>}
                {order.isMixed && <Badge color="violet">혼적</Badge>}
              </Flex>
            </ListRow>

            <ListHeader title="차량 정보" mt="xl" />
            <ListRow label="차량정보" val={order.driverCarInfo || "배차 전"} />
            <ListRow label="차량번호" val={order.driverCarNumber || "배차 전"} />
            {/* <ListRow label="차주정보" val={order.driverName || "배차 전"} /> */}
            <ListRow
              label="차주정보"
              val={
                order.driverPhoneNumber
                  ? `${order.driverName} (${parsePhone(order.driverPhoneNumber)})`
                  : "배차 전"
              }
            />
            <ListRow
              label="운송료"
              val={order.price ? parseMoney(order.price) + "원" : "배차 전"}
              isLast
            />

            <ListHeader title="배차/담당자 정보" mt="xl" />
            <ListRow
              label="담당자 정보"
              val={
                order?.user?.data
                  ? `${order?.user?.data?.attributes?.name} (${parsePhone(order?.user?.data?.attributes?.phoneNumber)})`
                  : "-"
              }
            />
            <ListRow label="상하차일시" val={order?.driverTime || "-"} />
            <ListRow label="화주알림" val={order?.ownerMemo || "-"} />
            <ListRow
              label="예상 거리/시간/통행료"
              val={
                order?.distance
                  ? `${order.distance}km / ${order.time}분 / ${parseMoney(order.fare)}원`
                  : "-"
              }
              isLast
            />
          </Grid.Col>
        </Grid>

        <Grid mt="sm" gutter="xs">
          <Grid.Col span={4} md={2}>
            <Button
              fullWidth
              disabled={order.status !== "requested" || order.billMonth}
              onClick={handleCancel}
              color="red"
            >
              화물 취소
            </Button>
          </Grid.Col>
          <Grid.Col span={4} md={2}>
            <Button
              fullWidth
              onClick={() => navigate(`/new/${id}`)}
              mr="auto"
              disabled={order.status !== "requested"}
            >
              배차 수정
            </Button>
          </Grid.Col>
          <Grid.Col span={4} md={2}>
            <OrderImagesViewer
              fullWidth
              receiptImage={order.receiptImage}
              orderImages={order.orderImages}
              label="인수증"
            />
          </Grid.Col>
          <Grid.Col span={4} md={2}>
            <OrderDetailBoseModal
              icUrl={order?.ic?.data?.attributes?.url}
              doUrl={order?.do?.data?.attributes?.url}
              plUrl={order?.pl?.data?.attributes?.url}
            />
          </Grid.Col>

          {user.permission === "admin" && (
            <Grid.Col span={4} md={2}>
              <Button fullWidth onClick={getDistance}>
                거리 계산
              </Button>
            </Grid.Col>
          )}
          {user.permission === "admin" && (
            <Grid.Col span={4} md={2}>
              <OrderDetailCompanyInfoModal companyId={companyId} />
            </Grid.Col>
          )}
        </Grid>
      </Modal>
    </>
  );
}
